import { useState, useEffect, useRef } from "react";
import { getBrowserSafeUrl } from "../../utils/groupDetail/urlHelpers";

export const useLightbox = (photos) => {
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHighResLoaded, setIsHighResLoaded] = useState(false);

  // Prevent stale closures while keeping a stable function reference
  const photosRef = useRef(photos);
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setPreviewPhoto(null);
  };

  const handleImageClick = (index) => {
    const currentPhotos = photosRef.current;
    setPreviewPhoto(currentPhotos[index]);
    setCurrentImageIndex(index);
    setIsModalOpen(true);
  };

  const handleNextImage = () => {
    const currentPhotos = photosRef.current;
    if (!currentPhotos || currentPhotos.length === 0) return;
    const nextIndex = (currentImageIndex + 1) % currentPhotos.length;
    setCurrentImageIndex(nextIndex);
    setPreviewPhoto(currentPhotos[nextIndex]);
  };

  const handlePrevImage = () => {
    const currentPhotos = photosRef.current;
    if (!currentPhotos || currentPhotos.length === 0) return;
    const prevIndex = (currentImageIndex - 1 + currentPhotos.length) % currentPhotos.length;
    setCurrentImageIndex(prevIndex);
    setPreviewPhoto(currentPhotos[prevIndex]);
  };

  const handleThumbnailClick = (index) => {
    const currentPhotos = photosRef.current;
    setCurrentImageIndex(index);
    setPreviewPhoto(currentPhotos[index]);
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isModalOpen) return;

    const handleKeyPress = (e) => {
      if (e.key === "Escape") handleModalClose();
      if (e.key === "ArrowRight") handleNextImage();
      if (e.key === "ArrowLeft") handlePrevImage();
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, currentImageIndex]);

  // Reset high-res loaded state when image changes
  useEffect(() => {
    setIsHighResLoaded(false);
  }, [currentImageIndex, previewPhoto]);

  // Preload adjacent images
  useEffect(() => {
    if (!isModalOpen) return;
    const currentPhotos = photosRef.current;
    if (!currentPhotos || currentPhotos.length === 0) return;
    
    const nextIndex = (currentImageIndex + 1) % currentPhotos.length;
    const prevIndex = (currentImageIndex - 1 + currentPhotos.length) % currentPhotos.length;

    const imgNext = new Image();
    imgNext.src = getBrowserSafeUrl(currentPhotos[nextIndex].filePath);
    
    const imgPrev = new Image();
    imgPrev.src = getBrowserSafeUrl(currentPhotos[prevIndex].filePath);
  }, [currentImageIndex, isModalOpen]);

  return {
    previewPhoto,
    currentImageIndex,
    isModalOpen,
    isHighResLoaded,
    setIsHighResLoaded,
    handleModalClose,
    handleImageClick,
    handleNextImage,
    handlePrevImage,
    handleThumbnailClick
  };
};
